/**
 * Heritage Dahlias, Admin Build-a-Box vouchers.
 *
 * Generate single-use voucher codes that a customer can redeem inside Heritage
 * HQ for a free Build-a-Box. Each code is one-time-use. When redeemed it shows
 * who claimed it and the date.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "./api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Copy, Trash2, Ticket, Plus } from "lucide-react";

export default function ConsoleVouchers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(6);
  const [label, setLabel] = useState("");
  const [count, setCount] = useState(1);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/vouchers");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Couldn't load vouchers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post("/admin/vouchers", {
        size: Number(size),
        label: label.trim(),
        count: Number(count) || 1,
      });
      toast.success(`Created ${r.data?.created || 0} voucher${r.data?.created === 1 ? "" : "s"}.`);
      setLabel("");
      setCount(1);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create voucher.");
    } finally {
      setCreating(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`${code} copied.`);
    } catch (e) {
      window.prompt("Copy the voucher code:", code);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete voucher ${code}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/vouchers/${code}`);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };

  return (
    <div data-testid="admin-vouchers" className="space-y-6">
      <div>
        <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-sage-deep)]">
          ✦ BUILD-A-BOX VOUCHERS ✦
        </span>
        <h1 className="font-display text-4xl md:text-5xl text-[var(--dahlias-forest)] mt-2 leading-[1.05]">
          Vou<span className="font-display-italic text-[#6E1E3A]">chers.</span>
        </h1>
        <p className="mt-3 text-sm text-[var(--dahlias-ink)]/70 max-w-xl">
          Generate single-use codes that customers can redeem in Heritage HQ for a free Build-a-Box.
          When redeemed, a new Heritage order appears in your manual orders queue with a
          customer-pick form ready to go.
        </p>
      </div>

      {/* Generator */}
      <section className="bg-[var(--dahlias-paper)] border-2 border-[#6E1E3A]/25 wobble-a p-5 md:p-6">
        <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">Create vouchers</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="font-label tracking-[0.25em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]">Box size</Label>
            <select
              data-testid="voucher-size"
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className="w-full mt-1.5 h-11 px-4 wobble-pill bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/30"
            >
              <option value={6}>6-pack</option>
              <option value={12}>12-pack</option>
            </select>
          </div>
          <div>
            <Label className="font-label tracking-[0.25em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]">Label (optional)</Label>
            <Input
              data-testid="voucher-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Birthday gift for Mum"
              className="mt-1.5 h-11 wobble-pill"
            />
          </div>
          <div>
            <Label className="font-label tracking-[0.25em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]">How many?</Label>
            <Input
              type="number"
              min={1}
              max={50}
              data-testid="voucher-count"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="mt-1.5 h-11 wobble-pill"
            />
          </div>
          <Button
            type="button"
            onClick={create}
            disabled={creating}
            data-testid="voucher-create"
            className="h-11 px-6 bg-[var(--berry)] hover:bg-[var(--berry-deep)] text-white wobble-pill inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> {creating ? "Creating…" : "Create"}
          </Button>
        </div>
      </section>

      {/* List */}
      <section>
        <h2 className="font-display text-2xl text-[var(--dahlias-forest)] mb-3">
          All vouchers{" "}
          <span className="text-[var(--dahlias-ink)]/45 text-base">({items.length})</span>
        </h2>
        {loading ? (
          <div className="text-[var(--dahlias-ink)]/55">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-[var(--dahlias-paper)] border-2 border-dashed border-[var(--dahlias-sage)]/30 p-10 text-center wobble-a">
            <Ticket className="w-10 h-10 text-[var(--dahlias-sage)] mx-auto mb-3" />
            <p className="text-sm text-[var(--dahlias-ink)]/65">
              No vouchers yet. Create one above to send a free Build-a-Box.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((v) => (
              <article
                key={v.code}
                data-testid={`voucher-${v.code}`}
                className={`bg-white border-2 wobble-a p-4 flex flex-wrap items-center justify-between gap-3 ${
                  v.status === "redeemed"
                    ? "border-[var(--dahlias-sage)]/40 opacity-80"
                    : "border-[#6E1E3A]/30"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="font-mono text-lg text-[#6E1E3A] font-bold tracking-wider">{v.code}</code>
                    <span
                      className={`px-2 py-0.5 wobble-pill text-[10px] uppercase tracking-[0.2em] ${
                        v.status === "redeemed"
                          ? "bg-[var(--dahlias-sage)] text-white"
                          : "bg-[var(--berry)]/15 text-[var(--berry-deep)]"
                      }`}
                    >
                      {v.status}
                    </span>
                    <span className="text-xs text-[var(--dahlias-ink)]/60">{v.size}-pack</span>
                  </div>
                  {v.label && (
                    <div className="text-sm text-[var(--dahlias-ink)]/80 mt-0.5">{v.label}</div>
                  )}
                  {v.status === "redeemed" && (
                    <div className="text-xs text-[var(--dahlias-ink)]/60 mt-0.5">
                      Redeemed by <strong>{v.redeemed_by_email}</strong>
                      {v.redeemed_at && ` on ${new Date(v.redeemed_at).toLocaleDateString("en-NZ")}`}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => copyCode(v.code)}
                    data-testid={`voucher-copy-${v.code}`}
                    className="h-9 px-3 text-xs inline-flex items-center gap-1.5 wobble-pill bg-[var(--dahlias-cream)] hover:bg-[var(--dahlias-sage)]/15 text-[var(--dahlias-ink)] border border-[var(--dahlias-sage)]/30"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                  {v.status !== "redeemed" && (
                    <button
                      type="button"
                      onClick={() => remove(v.code)}
                      data-testid={`voucher-delete-${v.code}`}
                      className="h-9 px-3 text-xs inline-flex items-center gap-1.5 wobble-pill bg-[var(--dahlias-rust)]/10 hover:bg-[var(--dahlias-rust)]/25 text-[var(--dahlias-rust)] border border-[var(--dahlias-rust)]/25"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
